import React from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Button, Paper, Box, Divider, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { getBroadcastThread, getSingleBroadcastMessage } from 'core/API/broadcast';
import { getToDoPrioritys } from 'core/API/todo';
import { formatDateTime12Hour } from 'shared/utils/dateUtils';
import { usePermissionCheck } from 'core/hooks/usePermissionCheck';
import BroadcastReplyActions, { BroadcastPriorityPill } from './components/BroadcastReplyActions';
import { getBroadcastReplyLocation } from './utils/broadcastNavigation';
import { BroadcastReplyMode, getPersonName, getReceiverLabel, getThreadRootId, isReplyAllAllowed, prependOriginalBroadcast, resolveParentTitle, sortThreadChronologically } from './utils/broadcastReply';

type RouteParams = { id?: string };
const preprocessContent = (html: string) => (html ? html.replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;') : html);
const priorityMembers = (data: any): any[] => {
	if (Array.isArray(data?.member)) return data.member;
	if (Array.isArray(data?.['hydra:member'])) return data['hydra:member'];
	if (Array.isArray(data?.items)) return data.items;
	if (Array.isArray(data)) return data;
	return [];
};
const priorityLabel = (message: any, priorities: any[]) => {
	if (!message?.priority) return 'N/A';
	if (typeof message.priority === 'object') return message.priority.label || message.priority.name || 'N/A';
	const id = String(message.priority).split('/').pop();
	const match = priorities.find((item) => String(item?.id) === id);
	return match?.label || match?.name || 'N/A';
};
const attachmentsOf = (message: any) => (Array.isArray(message?.attachments) ? message.attachments : message?.attachments?.member || []);

const ViewBroadcast: React.FC = () => {
	const history = useHistory();
	const { id } = useParams<RouteParams>();
	const [previewImage, setPreviewImage] = React.useState<{ url: string; alt: string } | null>(null);
	const { hasModuleViewPermission, hasModuleEditPermission } = usePermissionCheck();
	const canView = hasModuleViewPermission('Broadcast - My') || hasModuleViewPermission('Broadcast - All') || hasModuleViewPermission('Broadcast - Organization');
	const replyPermissions = {
		canCreate: hasModuleEditPermission('Broadcast - My') || hasModuleEditPermission('Broadcast - All') || hasModuleEditPermission('Broadcast - Organization'),
		canCreateOrganization: hasModuleEditPermission('Broadcast - Organization'),
		canCreateWorkplaceWide: hasModuleEditPermission('Broadcast - All'),
	};
	const { data: broadcastData, isLoading, error } = useQuery(['broadcast', id], () => getSingleBroadcastMessage(Number(id)), { enabled: !!id });
	const { data: prioritiesData } = useQuery(['todo-priorities'], () => getToDoPrioritys(), { staleTime: 5 * 60 * 1000 });
	const priorities = priorityMembers(prioritiesData);
	const threadRootId = getThreadRootId(broadcastData);
	const openedIsRoot = !broadcastData || Number(broadcastData.id) === threadRootId;
	const { data: rootBroadcast } = useQuery(['broadcast-reply-policy', threadRootId], () => getSingleBroadcastMessage(threadRootId), { enabled: !!broadcastData && !!threadRootId && !openedIsRoot, retry: false });
	const replyPolicy = openedIsRoot ? broadcastData : rootBroadcast;
	const allowReplyAll = isReplyAllAllowed(replyPolicy);
	const { data: threadData } = useQuery(['broadcast-thread', threadRootId], () => getBroadcastThread(threadRootId), { enabled: !!threadRootId && !!broadcastData, retry: false });
	const originalMessage = replyPolicy || (openedIsRoot ? broadcastData : null);
	const threadReplies = sortThreadChronologically(Array.isArray(threadData) ? threadData : []);
	const threadMessages = prependOriginalBroadcast(originalMessage, threadReplies);
	const back = () => history.push(ROUTES.BROADCAST);
	const handleReply = (mode: BroadcastReplyMode, threadMessage?: { id?: number; broadcastMessageId?: number | null }) => {
		if (!broadcastData?.id) return;
		const parentThreadMessageId = threadMessage?.broadcastMessageId && threadMessage.id
			? Number(threadMessage.id)
			: null;
		history.push(getBroadcastReplyLocation({
			replyToId: Number(broadcastData.id),
			replyMode: mode,
			threadRootId: getThreadRootId(broadcastData),
			parentThreadMessageId,
			replySource: parentThreadMessageId ? threadMessage : undefined,
			allowReplyAll,
		}));
	};
	if (!canView) return <Paper sx={{ p: 3 }}><Button style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }} onClick={back}>Back</Button><Typography color="error" sx={{ mt: 2 }}>You do not have permissions to view broadcasts.</Typography></Paper>;
	if (isLoading) return <Paper sx={{ p: 3 }}><Box display="flex" justifyContent="center" minHeight="400px"><CircularProgress /></Box></Paper>;
	if (error || !broadcastData) return <Paper sx={{ p: 3 }}><Button style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }} onClick={back}>Back</Button><Typography color="error" sx={{ mt: 2 }}>{error ? 'Error loading broadcast' : 'Broadcast not found'}</Typography></Paper>;
	const imageBase = (process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_URL || '').replace(/\/$/, '');
	return (
		<Paper elevation={1} sx={{ p: 3, maxWidth: '100%', margin: '0 auto' }}>
			<Box display="flex" alignItems="center" mb={2}>
				<Button style={{ backgroundColor: 'rgb(68, 99, 136)', color: 'white' }} onClick={back}>Back</Button>
			</Box>
			<Divider sx={{ mb: 3 }} />
			{threadReplies.length > 0 && <Typography sx={{ mb: 2, fontWeight: 600 }}>Conversation</Typography>}
			<Box display="flex" flexDirection="column" gap={2}>
				{threadMessages.map((message: any) => {
					const isThreadEntry = message?.broadcastMessageId != null || !!message?.broadcastMessage;
					const parentTitle = isThreadEntry ? resolveParentTitle(message, threadMessages) : null;
					const isActive = !isThreadEntry && String(message.id) === String(id);
					return (
						<Box key={isThreadEntry ? `thread-${message.id}` : `broadcast-${message.id}`} sx={{ border: isActive ? '1px solid rgb(68, 99, 136)' : '1px solid #ddd', borderRadius: 1, p: 2 }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
								<Typography variant="h5" sx={{ fontWeight: 'bold', fontSize: '1.25rem', flex: '1 1 160px', minWidth: 0 }}>{message.title || 'No Subject'}</Typography>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
									<BroadcastReplyActions
										size="small"
										message={replyPolicy || message}
										senderMessage={message}
										permissions={replyPermissions}
										allowReplyAll={allowReplyAll}
										onReply={(mode) => handleReply(mode, message)}
									/>
								</Box>
							</Box>
							{parentTitle && <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>In reply to: {parentTitle}</Typography>}
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2, pb: 2, borderBottom: '1px solid #eee' }}>
								<Typography><b>From:</b> {getPersonName(message.sender)}</Typography>
								<Typography><b>To:</b> {getReceiverLabel(message)}</Typography>
								<Box display="flex" alignItems="center" gap={1}><Typography component="span"><b>Priority:</b></Typography><BroadcastPriorityPill label={priorityLabel(message, priorities)} /></Box>
								<Typography><b>Date:</b> {(message.createdDate || message.createdAt || message.created_at) ? formatDateTime12Hour(message.createdDate || message.createdAt || message.created_at, message.createdTime || null) : 'N/A'}</Typography>
							</Box>
							{attachmentsOf(message).map((att: any) => {
								const fileName = att.fileName || att.name || `Attachment ${att.id ?? ''}`;
								const fileUrl = `${imageBase}/attachments/${fileName}`;
								const isImage = typeof att.mimeType === 'string' && att.mimeType.startsWith('image/');
								return isImage
									? <Box key={att.id || att['@id']} component="img" src={fileUrl} alt={fileName} onClick={() => setPreviewImage({ url: fileUrl, alt: fileName })} sx={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }} />
									: <Typography key={att.id || att['@id']} variant="body2"><a href={fileUrl} target="_blank" rel="noopener noreferrer">{fileName}</a></Typography>;
							})}
							<Box sx={{ '& .ql-toolbar': { display: 'none' }, '& .ql-container': { border: 'none' }, '& .ql-editor': { padding: 0 } }}>
								<ReactQuill value={preprocessContent(message.content || '')} readOnly theme="snow" modules={{ toolbar: false }} />
							</Box>
						</Box>
					);
				})}
			</Box>
			<Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>{previewImage?.alt}<IconButton onClick={() => setPreviewImage(null)} size="small"><Close /></IconButton></DialogTitle>
				<DialogContent>{previewImage && <Box component="img" src={previewImage.url} alt={previewImage.alt} sx={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }} />}</DialogContent>
			</Dialog>
		</Paper>
	);
};

export default ViewBroadcast;
